import * as React from 'react';
import { Route, Redirect, RouteProps } from 'react-router-dom';
import { isNullOrUndefined } from 'util';
import { useAuthenticatedState } from '../../api/api-authorization/useAuthenticatedState';
import { useCurrentUserRoles } from '../../api/account';

export interface AuthorizeRouteProps extends RouteProps {
    requireRole?: string | Array<string>,
}

/**
 * Route component that will check for authorization before showing its component.
 * 
 * NOTE this was originally based on AuthorizeRoute from Microsoft's template but converted to typescript and React hooks and to support more than just the component prop.
 * @param props
 */
export const AuthorizeRoute = (props: AuthorizeRouteProps) => {
    const { isAuthenticated } = useAuthenticatedState();
    const { data: { roles: usersRoles } } = useCurrentUserRoles();
    const [ hasRequiredRole, setHasRequiredRole ] = React.useState<boolean | null>(); 

    // Load the roles only if we need to.
    React.useEffect(() => {
        if (props.requireRole && !isNullOrUndefined(usersRoles)) {
            const requireRoles = [props.requireRole].flat(2);
            let hasRole = false;
            for (let roleToCheck of requireRoles) {
                if (usersRoles.find(it => it === roleToCheck)) {
                    hasRole = true;
                    break;
                }
            }
            setHasRequiredRole(hasRole);
        }
    }, [props.requireRole, usersRoles, setHasRequiredRole, isAuthenticated]);

    // Render the UI
    //
    const { component: Component, render, children, ...rest } = props;

    // Show nothing until we have a defined authenticated state.
    if (isNullOrUndefined(isAuthenticated)) {
        return (<></>);
    }
    
    // If we need to check a role, but can't yet return nothing.
    if (props.requireRole && isNullOrUndefined(usersRoles)) {
        return (<></>);
    }

    // If we don't have the role we require, show text saying so.
    if (hasRequiredRole === false) {
        return (<>Sorry you do not have permission to access this area.</>);
    }

    return (
        <Route {...rest}
            render={(props) => {
                if (isAuthenticated) {
                    if (Component) {
                        return <Component {...props} />;
                    } else if (render) {
                        return render(props);
                    } else {
                        return children;
                    }
                } else {
                    const redirectUrl = `/authentication/login?returnUrl=${encodeURIComponent(window.location.href)}`
                    return <Redirect to={redirectUrl} />
                }
            }} />
    );
};
